import Spinner from "components/Spinner";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Modal } from "react-bootstrap";
import * as xlsx from "xlsx";
import PreviewLabel from "./PreviewData/PreviewLabel";
import SelectExcelData from "./SelectExcelData/SelectExcelData";
import ViewSheet from "./ViewSheet/ViewSheet";
import SaveTemplate from "./LPTemplate/SaveTemplate";
import LoadTemplate from "./LPTemplate/LoadTemplate";

export default function LabelPrinter() {
  /*   All States
   ********************************************* */

  // Refs
  const fileInputRef = useRef(null);

  // Set when arriving from a work order attachment (see WOFiles).
  const location = useLocation();

  const [loading, setLoading] = useState(false);

  const [wb, setWb] = useState(null);
  const [ws, setWs] = useState(null);
  const [showWorkSheetModal, setShowWorkSheetModal] = useState(null);

  const [spreadsheetData, setSpreadsheetData] = useState([]);
  const [labelData, setLabelData] = useState(null);
  const [columnLabels, setColumnLabels] = useState([]);

  const [templateModal, setTemplateModal] = useState(false);
  const [templateLoadModal, setTemplateLoadModal] = useState(false);

  let noOfRows = 6;

  const [rowsData, setRowsData] = useState(
    Array.from({ length: noOfRows }, (_, i) => ({
      rowNo: i + 1,
      rowType: "manual",
      rowValues: {},
    }))
  );

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  const readExcel = (e) => {
    let file = e.target.files[0];
    loadingOn();
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      if (!file) {
        loadingOff();
        return;
      }
      fileReader.readAsArrayBuffer(file);

      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = xlsx.read(bufferArray, { type: "buffer" });
        resolve(wb);
        loadingOff();
      };

      fileReader.onerror = (error) => {
        reject(error);
        loadingOff();
      };
      loadingOff();
    });

    promise.then((d) => {
      setWb(d);
    });
  };

  function getDataFromSelectedSheet(sheetName) {
    resetEverything();
    let selectedSheet = wb.Sheets[sheetName];
    const sheetData = xlsx.utils.sheet_to_json(selectedSheet, { header: 1 });

    const [firstRow, ...restRows] = sheetData;
    setColumnLabels(firstRow || []);
    const formattedData = restRows.map((row) =>
      row.map((cell) => ({ value: cell, readOnly: true }))
    );

    setSpreadsheetData(formattedData);
    setShowWorkSheetModal(false);
  }

  const resetEverything = () => {
    setSpreadsheetData((prev) => []);
    setColumnLabels((prev) => []);
    setLabelData((prev) => null);
    setRowsData(
      Array.from({ length: noOfRows }, (_, i) => ({
        rowNo: i + 1,
        rowType: "manual",
        rowValues: {},
      }))
    );
  };

  const handleClearButton = () => {
    resetEverything();
    setWb(null);
    setWs(null);

    // Drop the handed-over attachment too. Without this, Clear wipes the sheet
    // and the effect above immediately reloads it from router state, so the
    // button would appear to do nothing.
    if (location.state?.fileKey) {
      window.history.replaceState({}, "");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Load a work-order attachment passed in via router state.
   *
   * The file lives in S3, not on disk, so it cannot come through the file
   * input: fetch a signed URL, pull the bytes, and hand the workbook to the
   * same `wb` state the file input sets. Everything downstream — the worksheet
   * picker, the column mapping — then behaves identically.
   *
   * xlsx.read parses CSV as well as xls/xlsx, so both work through this path.
   */
  const loadFromS3 = async (fileKey) => {
    try {
      loadingOn();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URI}/aws/images`,
        { key: fileKey },
      );
      const signedUrl = Array.isArray(data) ? data[0] : data;
      if (!signedUrl) throw new Error("Could not get a link to that file");

      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Could not download the file (HTTP ${response.status})`);
      }
      const buffer = await response.arrayBuffer();
      setWb(xlsx.read(buffer, { type: "buffer" }));
    } catch (err) {
      console.error("Label Printer: could not load the attachment", err);
      window.alert(
        "Could not open that attachment in the Label Printer: " + err.message,
      );
    } finally {
      loadingOff();
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    const fileKey = location.state?.fileKey;
    if (!fileKey) return;
    loadFromS3(fileKey);
    // Only when the handed-over file changes: re-running this on every render
    // would refetch the spreadsheet and throw away the user's column mapping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.fileKey]);

  useEffect(() => {
    if (wb) {
      resetEverything();
      setWs(wb.SheetNames);
      setShowWorkSheetModal(true);
    }
  }, [wb]);

  return (
    <div
      className={`flex-grow-1 h-100 px-5 py-2`}
      style={{
        position: "fixed",
        overflow: "auto",
        top: 0,
        width: "calc(100vw - 200px)",
        marginLeft: 210,
        zIndex: "99",
      }}
    >
      {loading && <Spinner />}
      <h1>Label Printer</h1>

      {/* ---- Extract Data and View Label ---- */}
      <div className="d-flex gap-2 align-items-center mb-3">
        {/* ---- Extract Data View Label ---- */}
        <div className="w-50 flex-grow-1">
          <div className="bg-white shadow-sm">
            <SelectExcelData
              columnLabels={columnLabels}
              spreadsheetData={spreadsheetData}
              setLabelData={setLabelData}
              rowsData={rowsData}
              setRowsData={setRowsData}
            />
          </div>
        </div>
        {/* ---- View Label ---- */}
        <div className="w-50 flex-grow-1">
          <div className="bg-white shadow-sm">
            <PreviewLabel
              columnLabels={columnLabels}
              labelData={labelData}
              rowsData={rowsData}
            />
          </div>
        </div>
      </div>
      {/* ---- View & Import Excel ---- */}
      <div className="row h-75">
        <div className="col-md-12">
          {/* ---- Action Bar ---- */}
          <div className="bg-success bg-opacity-10 px-3 py-2 rounded-3 shadow-sm d-flex align-items-center justify-content-between w-100 ">
            <h3 className="h3">Select Data</h3>
            <div className="d-flex align-items-center text-nowrap gap-2">
              <button
                onClick={(e) => {
                  setTemplateLoadModal(true);
                }}
                className="btn btn-sm btn-success"
              >
                Load Template
              </button>
              {spreadsheetData && spreadsheetData?.length > 0 && (
                <>
                  <button
                    onClick={(e) => {
                      setTemplateModal(true);
                    }}
                    className="btn btn-sm btn-primary"
                  >
                    Save Template
                  </button>
                  <button
                    onClick={(e) => {
                      setShowWorkSheetModal(true);
                    }}
                    className="btn btn-info btn-sm"
                  >
                    Change Sheet
                  </button>
                </>
              )}
              <label htmlFor="excelFile" className="h3">
                Select File
              </label>
              <input
                onChange={readExcel}
                type="file"
                ref={fileInputRef}
                className="form-control d-inline-block"
              />
              <button
                onClick={(e) => {
                  handleClearButton();
                }}
                className="btn btn-danger btn-sm"
              >
                Clear
              </button>
            </div>
          </div>
          {/* ---- View Excel ---- */}
          <div className="overflow-auto mt-3 bg-white shadow-sm rounded-3">
            <ViewSheet
              columnLabels={columnLabels}
              spreadsheetData={spreadsheetData}
            />
          </div>
        </div>
      </div>
      {/* Select Excel Sheet */}
      <Modal
        show={showWorkSheetModal}
        onHide={() => setShowWorkSheetModal(false)}
      >
        <Modal.Body className="bg-light">
          <h3>Select WorkSheet</h3>
          <div className="my-3">
            {ws &&
              ws.map((item) => (
                <div
                  key={item}
                  className="bg-white shadow-sm custom-border-radius-sm p-1 px-2 mb-1 d-flex align-items-center justify-content-between"
                >
                  <div> {item}</div>
                  <div>
                    <button
                      onClick={() => {
                        getDataFromSelectedSheet(item);
                      }}
                      className="btn btn-sm btn-success"
                    >
                      select
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="text-end">
            <button
              onClick={() => {
                setShowWorkSheetModal(false);
              }}
              className="btn btn-sm btn-secondary"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Export to work-order template */}
      <Modal
        show={templateModal}
        onHide={() => setTemplateModal(false)}
        contentClassName="bg-transparent"
      >
        <Modal.Body className="bg-light rounded-3 shadow-sm">
          <h3>Select WorkSheet</h3>

          {/* Modal Body */}
          <SaveTemplate
            showModal={setTemplateModal}
            rowsData={rowsData}
            columnLabels={columnLabels}
            spreadsheetData={spreadsheetData}
          />
        </Modal.Body>
      </Modal>
      {/* Load Data to work-order template */}
      <Modal
        show={templateLoadModal}
        onHide={() => setTemplateLoadModal(false)}
        contentClassName="bg-transparent"
      >
        <Modal.Body className="bg-light rounded-3 shadow-sm">
          <h3>Select WorkSheet</h3>

          {/* Modal Body */}
          <LoadTemplate
            showModal={setTemplateLoadModal}
            setRowsData={setRowsData}
            setColumnLabels={setColumnLabels}
            setSpreadsheetData={setSpreadsheetData}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
